import React, { useEffect, useState } from "react";
import "../../components/dashboard/newDashboard.css";
import ManagementDashboard from "../../components/dashboard/ManagementDashboard";
import { errorMessage, successMessage } from "../../utils/notificationUtils";
import FiltersManagementDashboard from "./FiltersManagementDashboard";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  resetHospitalToSchemeCodesMap,
  updateSelectedHospital,
} from "../../models/hospitalSlice";
import { resetAddIPDBillState } from "../../models/ipdBillsSlice";
import dayjs from "dayjs";
import { LIST_ORDER, MANAGEMENT_DASHBOARD } from "../../utils/constants";
import { Skeleton } from "antd";

const mapWidgetTitleApis: any = {
  business: MANAGEMENT_DASHBOARD.business,
  split: MANAGEMENT_DASHBOARD.split,
  businessTrend: MANAGEMENT_DASHBOARD.businessTrend,
  schemeWiseSettlement: MANAGEMENT_DASHBOARD.schemeWiseSettlement,
  settlementTrend: MANAGEMENT_DASHBOARD.settlementTrend,
  dso: MANAGEMENT_DASHBOARD.dso,
  businessTillDate: MANAGEMENT_DASHBOARD.businessTillDate,
  settledTillDate: MANAGEMENT_DASHBOARD.settledTillDate,
  settledSplit: MANAGEMENT_DASHBOARD.settledSplit,
};

const NewDashboardContainer = () => {
  const { hospital, auth } = useSelector((state: RootState) => state);
  const dispatch = useDispatch<AppDispatch>();
  const [widgets, setWidgets] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const columnKeyTitles = [
    { key: "business", title: "Business" },
    { key: "split", title: "Split" },
    { key: "businessTrend", title: "Business Trend" },
    { key: "schemeWiseSettlement", title: "Scheme Wise Settlement" },
    { key: "settlementTrend", title: "Settlement Trend" },
    { key: "dso", title: "DSO" },
    { key: "businessTillDate", title: "Business Till Date" },
    { key: "settledTillDate", title: "Settled Till Date" },
    { key: "settledSplit", title: "Settled Split" },
  ];
  const [selectedColumnKeys, setSelectedColumnKeys] = useState<string[]>(
    columnKeyTitles?.map((col: any) => col?.key as string)
  );

  const getMonthRange = (monthYear: string) => {
    const [month, year] = monthYear.split("-").map(Number);

    const date = dayjs(`${year}-${month}-01`);

    const startDate = date.startOf("month").format("DD-MM-YYYY");

    const endDate = date.endOf("month").format("DD-MM-YYYY");

    return { startDate, endDate };
  };

  const getQuarterRange = (quarterYear: string) => {
    // quarterYear format: "Qn-YYYY" e.g., Q1-2025
    const [qStr, yearStr] = quarterYear.split("-");
    const year = Number(yearStr);
    const quarter = Number(qStr?.replace("Q", ""));
    const quarterToStartMonth: Record<number, number> = {
      1: 1, // Jan
      2: 4, // Apr
      3: 7, // Jul
      4: 10, // Oct
    };
    const startMonth = quarterToStartMonth[quarter];
    const start = dayjs(`${year}-${String(startMonth).padStart(2, "0")}-01`);
    const startDate = start.startOf("month").format("DD-MM-YYYY");
    const endDate = start.add(2, "month").endOf("month").format("DD-MM-YYYY");
    return { startDate, endDate };
  };

  const fetchData = async (urls: Array<string>, appliedFilters?: any) => {
    try {
      setIsLoading(true);
      const usedFilters = appliedFilters || filters;

    //   const { startDate, endDate } = usedFilters?.quarter
    //     ? getQuarterRange(usedFilters?.quarter)
    //     : usedFilters?.monthYear
    //     ? getMonthRange(usedFilters?.monthYear)
    //     : {
    //         startDate: usedFilters.startDate,
    //         endDate: usedFilters.endDate,
    //       };

    const { startDate, endDate } = usedFilters?.monthYear
        ? getMonthRange(usedFilters?.monthYear)
        : {
            startDate: usedFilters.startDate,
            endDate: usedFilters.endDate,
          };

      const results = await Promise.all(
        urls?.map((url) => {
          let customStartDate = startDate;
          let customEndDate = endDate;
          if (url?.toLowerCase()?.includes("trend")) {
            // For trend, use start of selected quarter (if quarter present),
            // otherwise fallback to month range startDate calculated above
            if (usedFilters?.quarter) {
              const { startDate: qStart, endDate: qEnd } = getQuarterRange(
                usedFilters.quarter
              );
              customStartDate = qStart;
              customEndDate = qEnd;
            }
          }

          return fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              hospitalId: hospital?.selectedHospital?.value,
              startDate: customStartDate,
              endDate: customEndDate,
            }),
          }).then((r) => r.json());
        })
      );

      setWidgets(results?.map((res) => res.data));
      setIsLoading(false);
    } catch (err) {
      errorMessage("Please, Try again later");
      setIsLoading(false);
      console.error("Error fetching data:", err);
    }
  };

  //   const fetchData = async (urls: Array<string>, appliedFilters?: any) => {
  //     try {
  //       setIsLoading(true);
  //       const usedFilters = appliedFilters || filters;
  //       const { startDate, endDate } = usedFilters?.monthYear
  //         ? getMonthRange(usedFilters?.monthYear)
  //         : {
  //             startDate: usedFilters.startDate,
  //             endDate: usedFilters.endDate,
  //           };

  //       const results = await Promise.all(
  //         urls?.map((url) =>
  //           fetch(url, {
  //             method: "POST",
  //             headers: {
  //               "Content-Type": "application/json",
  //             },
  //             body: JSON.stringify({
  //               hospitalId: hospital?.selectedHospital?.value,
  //               startDate,
  //               endDate,
  //             }),
  //           }).then((r) => r.json())
  //         )
  //       );

  //       setWidgets(results?.map((res) => res.data));
  //       setIsLoading(false);
  //     } catch (err) {
  //       errorMessage("Please, Try again later");
  //       setIsLoading(false);
  //       console.error("Error fetching data:", err);
  //     }
  //   };

  const handleHospitalChange = (hospitalId: number) => {
    if (hospital.selectedHospital?.value !== hospitalId) {
      dispatch(updateSelectedHospital(hospitalId));
      dispatch(resetAddIPDBillState());
      dispatch(resetHospitalToSchemeCodesMap());
      successMessage("HOSPITAL UPDATE");
    }
  };

  const [filters, setFilters] = useState<any>({
    startDate: null,
    endDate: null,
    orderBy: "DESC",
    monthYear: null,
    quarter: null,
  });

  const handleFiltersChange = (newFilters?: any) => {
    const appliedFilters = newFilters || filters;
    if (hospital?.selectedHospital?.value) {
      const urls = selectedColumnKeys?.map((key) => mapWidgetTitleApis[key]);
      fetchData(urls, appliedFilters);
    }
  };

  //   const handleFiltersChange = () => {
  //     if (hospital?.selectedHospital?.value) {
  //       const urls = selectedColumnKeys?.map((key) => mapWidgetTitleApis[key]);
  //       fetchData(urls);
  //     }
  //   };

  useEffect(() => {
    if (auth?.serverTime) {
      const currDate = dayjs(auth?.serverTime).format("DD-MM-YYYY");
      const startDate = dayjs(auth.serverTime)
        .subtract(1, "week")
        .format("DD-MM-YYYY");
      const monthYear = filters?.monthYear
        ? dayjs(filters?.monthYear, "MM-YYYY").format("MM-YYYY")
        : dayjs(auth.serverTime).format("MM-YYYY");

      const monthYearDayjs = dayjs(monthYear, "MM-YYYY");

      const derivedQuarter = `Q${Math.ceil(
        Number(monthYearDayjs.format("M")) / 3
      )}-${monthYearDayjs.format("YYYY")}`;
      const initialPatientFilters: any = {
        endDate: currDate,
        orderBy: LIST_ORDER.DESC,
        startDate: startDate,
        monthYear: monthYear,
        quarter: derivedQuarter,
      };
      setFilters(initialPatientFilters);
    }
  }, [auth.serverTime, hospital?.selectedHospital?.value]);

  useEffect(() => {
    if (
      selectedColumnKeys.length > 0 &&
      hospital?.selectedHospital?.value &&
      filters?.startDate &&
      filters?.endDate
    ) {
      const urls = selectedColumnKeys?.map((key) => mapWidgetTitleApis[key]);
      fetchData(urls, filters);
    }
  }, [hospital?.selectedHospital?.value, selectedColumnKeys, filters]);

  return (
    // <div>
    //     <NewDashboard />
    // </div>
    <>
      {isLoading ? (
        <Skeleton />
      ) : (
        <div>
          <div
            style={{
              display: "flex",
              //   justifyContent: "space-between",
              //   alignItems: "center",
              flexDirection: "column",
              width: "100%",
              marginTop: "-10px",
            }}
          >
            <h2>Management Dashboard</h2>
            <FiltersManagementDashboard
              columnKeyTitles={columnKeyTitles}
              availableHospitals={hospital.availableHospitals}
              selectedHospital={hospital.selectedHospital}
              selectedColumnKeys={selectedColumnKeys}
              setSelectedColumnKeys={setSelectedColumnKeys}
              handleHospitalChange={handleHospitalChange}
              filters={filters}
              handleFiltersChange={handleFiltersChange}
              setFilters={setFilters}
            />
          </div>
          <div className="dashboard-grid">
            {widgets?.map((widget, idx) => (
              <ManagementDashboard
                key={idx}
                widgetTitle={widget?.widgetTitle}
                widgetType={widget?.widgetType}
                widgetItems={widget?.widgetItems}
              />
            ))}
            <div className="dashboard-card full-width">
              <h3>Deduction Analysis</h3>
              <div className="placeholder"></div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NewDashboardContainer;
